// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Select from 'react-select';

import api from '../../../api';

// Utilities
import formatDate from '../../../utilities/formatDate';
import plural from '../../../utilities/plural';
import { encodeURL } from '../../../utilities/url';

// Styles
import '../../../styles/fields.css';
import ContextMenu from '../../ContextMenu';
import { toast } from 'react-toastify';

const VALUES_LIMIT = 100;

export default class MyFields extends React.Component {

    state = {
        sortBy: "",
        sortDir: 1, // 1: Ascending, -1, Descending
        filter: "",
        skip: 0,
        count: 0,
        rawCount: 0
    }

    sort = (values) => {
        let { sortBy, sortDir } = this.state;

        if (values)
            values = values.filter(v => String(v._id).toLowerCase().includes(this.state.filter));

        if (sortBy === "")
            return values;

        let sorted = values.slice();
        sorted.sort((a,b) => a[sortBy] > b[sortBy] ? sortDir : a[sortBy] < b[sortBy] ? -sortDir : 0);
        return sorted;
    }

    toggleHeader = (key) => async () => {
        const sortDir = this.state.sortBy === key ? (this.state.sortDir > 0 ? -1 : 1) : 1;
        if (this.state.skip > 0 || this.state.count >= VALUES_LIMIT)
            await this.selectField(this.props.field.key, 0, this.state.filter, key, sortDir);
        await this.setState({
            sortBy: key,
            sortDir
        });
    }

    getSortArrow = (key) => {
        if (this.state.sortBy !== key)
            return "";

        return this.state.sortDir > 0 ? <>▴&nbsp;&nbsp;</> : <>▾&nbsp;&nbsp;</>;
    }

    componentDidMount = async () => {
        await this.props.loading();
        const res = await api.get('/field/all');
        const fields = res.fields.filter(f => f.length > 0);
        if (res && res.fields && fields.length > 0) {
            await this.props.setFields(fields);
            await this.selectField(fields.includes('pid') ? 'pid' : fields[0]); // Select first field
        }
        this.props.loaded();
    }

    setSkip = async (skip) => {
        await this.selectField(this.props.field.key, skip, this.state.filter, this.state.sortBy, this.state.sortDir);
    }

    filterField = async (filter) => {
        await this.selectField(this.props.field.key, 0, filter, this.state.sortBy, this.state.sortDir);
    }

    openDocuments = (data) => {
        const newUrl = '/field/' + encodeURL(this.props.field.key) + '/' + encodeURL(data.value._id);
        this.props.history.push(newUrl);
    }

    openDocumentsInNewTab = (data) => {
        const newUrl = '/field/' + encodeURL(this.props.field.key) + '/' + encodeURL(data.value._id);
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', newUrl);
        linkElement.setAttribute('target', '_blank');
        linkElement.click();
    }

    removeFieldFromDocuments = async (data) => {
        const response = await api.get(`/field/remove?field=${this.props.field.key}&value=${data.value._id}`);
        if (response && response.success) {

            // Remove the entry for this value from our field
            this.props.selectField({
                ...this.props.field,
                values: this.props.field.values.filter((o) => o._id !== data.value._id),
            });
            
            if (response.modified !== undefined) {
                toast.success(`Field removed from ${response.modified} document${response.modifiedCount === 1 ? '' : 's'}.`);
            }
        }
    }

    selectField = async (field, skip=0, filter=null, sort=null, dir=1) => {
        await this.props.loading();
        const res = await api.get('/field/list?field=' + encodeURIComponent(field) + '&skip=' + skip + (filter && filter.length > 0 ? '&filter=' + filter : '') + (sort ? '&sort=' + sort + '&sortDir=' + dir : ''));
        if (res && res.values) {
            await this.props.selectField({
                key: res.field,
                values: res.values,
                unset: res.unset
            });
            const count = res.count ? res.count : 0;
            await this.setState({
                sortBy: sort ? sort : "",
                sortDir: dir,
                filter: filter ? filter : "",
                skip,
                count,
                rawCount: !filter || filter === "" ? count : this.state.rawCount
            });
        }
        this.props.loaded();
    }

    renderHeader = (label, key, className="stack-list-header-item", style={}) => (<div
        className={className + " stack-list-header-link"}
        title="Click to sort by key"
        onClick={this.toggleHeader(key)}
        style={style}
    >{this.getSortArrow(key)}{label}</div>)

    render = () => {
        const values = this.sort(this.props.field.values);
        return (<div className="files">
            <div className="field-select-container">
                <label>Filter by field:</label>
                <div style={{
                    width: "calc(50% - 70px)",
                    float: 'left'
                }}>
                    <Select
                        className="select-container"
                        classNamePrefix="select"
                        value={(this.props.field && this.props.field.key) ? {
                            value: this.props.field.key,
                            label: this.props.field.key.toUpperCase()
                        } : {
                            value: "",
                            label: "Select a field..."
                        }}
                        options={this.props.fields.map(f => ({
                            value: f,
                            label: f.toUpperCase()
                        }))}
                        isSearchable={true}
                        placeholder="Select a field..."
                        onChange={option => this.selectField(option.value)}
                    />
                </div>
                <div style={{
                    width: "calc(50% - 80px)",
                    float: 'left',
                    marginLeft: 24
                }}>
                    <input
                        className="field-input"
                        type="text"
                        placeholder="Filter value..."
                        value={this.state.filter}
                        onChange={async e => {
                            if (this.filterTimeout)
                                clearTimeout(this.filterTimeout);
                            const filter = e.target.value.toLowerCase();
                            await this.setState({ filter });
                            if (this.state.rawCount >= VALUES_LIMIT)
                                this.filterTimeout = setTimeout(() => this.filterField(filter), 500);
                        }}
                    />
                </div>
            </div>

            <div style={{ clear: 'both' }} />

            <h1>Field Summary</h1>

            {this.state.count >= VALUES_LIMIT || this.state.skip > 0 ? <div className="fields-pagination">
                {this.state.skip > 0 && <div className="fields-pagination-control" onClick={() => this.setSkip(0)}>«</div>}
                {this.state.skip > 0 && <div className="fields-pagination-control" onClick={() => this.setSkip(this.state.skip - VALUES_LIMIT > 0 ? this.state.skip - VALUES_LIMIT : 0)}>‹</div>}
                <div>{this.state.skip+1}-{this.state.skip+VALUES_LIMIT > this.state.count ? this.state.count : this.state.skip+VALUES_LIMIT} of {this.state.count}</div>
                {this.state.skip < this.state.count - VALUES_LIMIT && <div className="fields-pagination-control" onClick={() => this.setSkip(this.state.skip + VALUES_LIMIT)}>›</div>}
                {this.state.skip < this.state.count - VALUES_LIMIT && <div className="fields-pagination-control" onClick={() => this.setSkip(VALUES_LIMIT * Math.floor(this.state.count/VALUES_LIMIT))}>»</div>}
            </div> : ""}

            <div className="stack-list-header">
                {this.renderHeader(this.props.field.key.toUpperCase(), "_id", "stack-list-header-left")}
                <div className="stack-list-meta">
                    {this.renderHeader("Files", "numDocuments")}
                    {this.renderHeader("Unread", "numUnread")}
                    {this.renderHeader("Last Modified", "lastModified", "stack-list-header-item", { width: '120px' })}
                </div>
            </div>
            {!values || values.length === 0 ? <>
            
            </> : values.map((value,idx) => <div
                key={idx}
                className="stack stack-list"
                style={{
                    backgroundImage: "url('https://static.stax.ai/images/icons/" + (this.props.user && this.props.user.enableDarkMode ? "dark/" : "") + "folder-field.png')"
                }}
                onClick={() => this.props.history.push('/field/' + encodeURL(this.props.field.key) + '/' + encodeURL(value._id))}
                onContextMenu={(event) => this.openMenu(event, {
                    field: this.props.field,
                    value: value,
                })}
                title={value._id}
            >
                <h1>{value._id}</h1>
                <div className="stack-list-meta">
                    <h2>{value.numDocuments}</h2>
                    <h3>{value.numUnread}</h3>
                    <div className="stack-list-meta-col">{formatDate(value.lastModified)}</div>
                </div>
            </div>)}

            {/* Documents without field set */}
            {this.props.field && this.props.field.unset ? <div className="fields-unset">
                <strong>{this.props.field.unset}</strong> {plural("document", this.props.field.unset)} without key set. &emsp;<a href={'/field/' + this.props.field.key + '/___unset___'}>View »</a>
            </div> : ""}

            <ContextMenu
                load={(open, close) => {
                    this.openMenu = open;
                    this.closeMenu = close;
                }}
                options={[{
                    label: 'View Documents',
                    action: this.openDocuments,
                },{
                    label: 'Open in new tab',
                    action: this.openDocumentsInNewTab,
                },{
                    label: 'Remove Field from Documents',
                    action: this.removeFieldFromDocuments,
                },
            ]}/>
        </div>);
    }

};